import { QuickKeyNodeType } from './qk.constants'
import { Direction, QuickKey, QuickKeyFolder, QuickKeyRoot } from './qk-types'

interface PositionData {
  pageIndex: number
  position: number
}

/**
 * Navigate the given grid of quick keys according to the given navigation data.
 *
 * @param  rootNode
 *         The root node of the tree of quick keys being navigated around
 *
 * @param  navigationData
 *
 *         @param navigationData.fromPosition
 *                The position (`pageIndex`, `position`) in the grid to navigate from
 *
 *         @param [navigationData.toPosition]
 *                The position (`pageIndex`, `position`) in the grid to navigate to. Calculated if not supplied
 *
 *         @param [navigationData.to]
 *                The quick key tree node in the grid to navigate from. Calculated if not supplied
 *
 *         @param [navigationData.direction]
 *                The direction (LEFT, RIGHT, UP, DOWN) to navigate in. Defaults to RIGHT
 *
 * @param  [options]
 *
 *         @param [options.includeBlanks=false]
 *                Whether to include blank nodes in the nodes that can be navigated to
 *
 *         @param [options.excludeNonBlanks=false]
 *                Whether to exclude non-blank nodes in the nodes that can be navigated to (i.e. only navigate to
 *                blank nodes)
 *
 * @return The complete navigation data (`from`, `to`, `fromPosition`, `toPosition`) describing the
 *         navigation between quick keys. If a valid node to navigate to could not be found then `to` and `toPosition`
 *         will be `null`.
 */
export const navigate = (
  rootNode: QuickKeyRoot | QuickKeyFolder,
  navigationData: {
    fromPosition: PositionData
    toPosition?: PositionData | null
    to?: QuickKey | null
    direction?: Direction
  },
  { includeBlanks = false, excludeNonBlanks = false } = {}
) => {
  const { direction } = navigationData
  const fromPosition = navigationData.fromPosition || {
    pageIndex: 0,
    position: -1,
  }
  const from = getQuickKeyAtPosition(rootNode, fromPosition)

  let { toPosition, to } = navigationData
  let firstSeenNode: QuickKey | undefined

  const shouldContinueNavigating = (quickKey: QuickKey) => {
    const isBlank = quickKey.type === QuickKeyNodeType.BLANK
    return (
      quickKey.locked ||
      (!includeBlanks && isBlank) ||
      (excludeNonBlanks && !isBlank)
    )
  }

  const findNextToPosition = (fromPos: PositionData) => {
    let positionData: PositionData | null = fromPos

    do {
      positionData = nextPosition(rootNode, positionData, direction)
      to = positionData && getQuickKeyAtPosition(rootNode, positionData)

      if (!to || firstSeenNode === to) {
        // Seen every node in the collection of pages and not found a suitable node to navigate to, break the loop
        positionData = null
      } else if (!firstSeenNode) {
        firstSeenNode = to
      }

      if (!positionData) {
        // A valid position could not be found, break the loop and return null/null
        positionData = null
        to = null
        break
      }
    } while (shouldContinueNavigating(to!))

    toPosition = positionData
  }

  if (!toPosition && !to) {
    findNextToPosition(fromPosition)
  } else if (!to) {
    to = getQuickKeyAtPosition(rootNode, toPosition!)

    if (shouldContinueNavigating(to!)) {
      findNextToPosition(toPosition!)
    }
  }

  return { from, fromPosition, to, toPosition }
}

/**
 * Get the quick key node in the given grid of quick keys located at the given position.
 *
 * @param  rootNode
 *         The root node of the tree of quick keys being navigated around
 *
 * @param  positionData
 *         The position data (`pageIndex`, `position`) representing the location of the quick key node
 *
 * @return  The quick key tree node or `undefined` if it could not be found
 */
const getQuickKeyAtPosition = (
  rootNode: QuickKeyFolder | QuickKeyRoot,
  positionData: PositionData
): QuickKey | undefined => {
  const page = rootNode.children[positionData.pageIndex]
  return page && page.viewData.nodes[positionData.position]
}

/**
 * Calculate the next position in the given grid of quick keys by moving in the given direction from the given
 * current position.
 *
 * @param  rootNode
 *         The root node of the tree of quick keys being navigated around
 *
 * @param  fromPosition
 *         The current position (`pageIndex`, `position`) in the grid
 *
 * @param  [direction]
 *         Direction to move in to the next position (defaults to 'RIGHT')
 *
 * @return The calculated next position (`pageIndex`, `position`)
 */
const nextPosition = (
  rootNode: QuickKeyFolder | QuickKeyRoot,
  fromPosition: PositionData,
  direction?: Direction
) => {
  const pages = rootNode.children

  if (!pages.length) {
    // This layout/folder has no pages and is empty - return null as there is no nodes to navigate to
    return null
  }

  const lastPageIndex = pages.length - 1

  let { pageIndex, position } = fromPosition
  let pageSize!: number
  let rowSize!: number

  function setPageVariables() {
    const page = pages[pageIndex]
    pageSize = page.viewData.nodes.length
    rowSize = page.viewData.pageDimensions.rowSize
  }

  function bumpPage() {
    if (direction === 'RIGHT' || direction === 'DOWN') {
      pageIndex = pageIndex < lastPageIndex ? pageIndex + 1 : 0
    } else {
      pageIndex = pageIndex > 0 ? pageIndex - 1 : lastPageIndex
    }

    setPageVariables()
  }

  setPageVariables()

  switch (direction) {
    case 'UP':
      if (position >= rowSize) {
        position -= rowSize
      } else {
        bumpPage()
        position = pageSize - rowSize + position
        if (pageIndex === lastPageIndex) {
          position -= 1
          if (position < pageSize - rowSize) {
            position = pageSize - 1
          }
        }
      }
      break
    case 'DOWN':
      if (position < pageSize - rowSize) {
        position += rowSize
      } else {
        bumpPage()
        position = (pageIndex === 0 ? position + 1 : position) % rowSize
      }
      break
    case 'LEFT':
      if (position > 0) {
        position -= 1
      } else {
        bumpPage()
        position = pageSize - 1
      }
      break
    case 'RIGHT':
    // falls through
    default:
      if (position < pageSize - 1) {
        position += 1
      } else {
        bumpPage()
        position = 0
      }
      break
  }

  return { pageIndex, position }
}
